import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, LucideIcon } from 'lucide-react';

/** Page wrapper: width limit and the redesigned typeface. */
export const Page: React.FC<{ className?: string; wide?: boolean; children: React.ReactNode }> = ({
  className = '',
  wide = false,
  children,
}) => (
  <div className={`mx-auto w-full font-ui text-ink antialiased ${wide ? 'max-w-none' : 'max-w-[1200px]'} ${className}`}>
    {children}
  </div>
);

interface PageHeaderProps {
  title: React.ReactNode;
  /** One short line under the title, e.g. a count. Avoid descriptions. */
  meta?: React.ReactNode;
  /** Back link shown above the title on sub-pages; `onClick` replaces navigation (e.g. to confirm leaving) */
  back?: { to: string; label: string; onClick?: (event: React.MouseEvent) => void };
  actions?: React.ReactNode;
  className?: string;
}

export const PageHeader: React.FC<PageHeaderProps> = ({ title, meta, back, actions, className = '' }) => (
  <header className={`mb-5 ${className}`}>
    {back && (
      <Link
        to={back.to}
        onClick={back.onClick}
        className="-ml-1 mb-2 inline-flex h-7 max-w-full items-center gap-0.5 rounded-full pl-0.5 pr-2 text-[13px] text-neutral-500 transition-colors hover:text-ink"
      >
        <ChevronLeft className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
        {/* one line: a long record name must not run into the title */}
        <span className="truncate">{back.label}</span>
      </Link>
    )}
    <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
      <div className="min-w-0">
        {/* up to two lines, so a long record or issue name stays readable on a phone */}
        <h1 className="line-clamp-2 break-words text-[24px] font-normal leading-tight tracking-[-0.03em] text-ink">{title}</h1>
        {meta && <p className="mt-1 text-[13px] text-neutral-500">{meta}</p>}
      </div>
      {actions && <div className="flex flex-shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  </header>
);

export const SectionHeader: React.FC<{
  title: React.ReactNode;
  aside?: React.ReactNode;
  className?: string;
}> = ({ title, aside, className = '' }) => (
  <div className={`mb-2.5 flex min-h-[28px] items-center justify-between gap-3 ${className}`}>
    <h2 className="text-[15px] font-normal tracking-[-0.01em] text-ink">{title}</h2>
    {aside && <div className="flex items-center gap-2 text-[12px] text-neutral-400">{aside}</div>}
  </div>
);

/**
 * Bordered white surface. Use `divided` for stacked rows.
 * It does not clip overflow, so dropdowns and menus inside it stay visible; the first and last
 * direct children get rounded corners so row hover backgrounds follow the border. Pass `clip`
 * for content that must be clipped (e.g. an image flush to the edge).
 */
export const Card: React.FC<
  React.HTMLAttributes<HTMLDivElement> & { padded?: boolean; divided?: boolean; clip?: boolean }
> = ({ padded = false, divided = false, clip = false, className = '', children, ...props }) => (
  <div
    className={`rounded-2xl border border-neutral-200 bg-white [&>:first-child]:rounded-t-[15px] [&>:last-child]:rounded-b-[15px] ${
      clip ? 'overflow-hidden' : ''
    } ${padded ? 'p-4' : ''} ${divided ? 'divide-y divide-neutral-200/70' : ''} ${className}`}
    {...props}
  >
    {children}
  </div>
);

export const EmptyState: React.FC<{
  icon?: LucideIcon;
  title: string;
  action?: React.ReactNode;
  className?: string;
}> = ({ icon: Icon, title, action, className = '' }) => (
  <div className={`flex flex-col items-center justify-center gap-3 px-4 py-10 text-center ${className}`}>
    {Icon && (
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-neutral-100 text-neutral-400">
        <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
      </span>
    )}
    <p className="text-[13px] text-neutral-500">{title}</p>
    {action}
  </div>
);

export const Skeleton: React.FC<{ className?: string }> = ({ className = 'h-4 w-full' }) => (
  <div className={`animate-pulse rounded-lg bg-neutral-100 ${className}`} aria-hidden="true" />
);

export interface StatItem {
  label: string;
  value: React.ReactNode;
  /** Small pill after the value, e.g. a delta */
  badge?: React.ReactNode;
  hint?: React.ReactNode;
  to?: string;
  /** Small leading mark, e.g. an <EntityIcon size="xs" /> */
  icon?: React.ReactNode;
}

// Literal class names so Tailwind generates them
const LG_COLUMNS: Record<number, string> = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
};

/** Row of numbers in one bordered card split by hairlines, as in the reference overview. */
export const StatGrid: React.FC<{ items: StatItem[]; className?: string }> = ({ items, className = '' }) => (
  <div
    className={`grid grid-cols-2 overflow-hidden rounded-2xl border border-neutral-200 bg-white ${
      LG_COLUMNS[Math.min(items.length, 4)]
    } ${className}`}
  >
    {items.map((item, index) => {
      const body = (
        <>
          <p className="flex min-w-0 items-center gap-2 text-[12px] text-neutral-500">
            {item.icon}
            <span className="truncate">{item.label}</span>
          </p>
          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="truncate text-[22px] leading-none tracking-[-0.02em] tabular-nums text-ink">{item.value}</span>
            {item.badge}
          </div>
          {item.hint && <p className="mt-2 truncate text-[12px] text-neutral-400">{item.hint}</p>}
        </>
      );
      const cellClass = `block p-4 ${index % 2 === 1 ? 'border-l' : ''} ${index >= 2 ? 'border-t lg:border-t-0' : ''} ${
        index > 0 ? 'lg:border-l' : ''
      } border-neutral-200/70`;
      return item.to ? (
        <Link key={item.label} to={item.to} className={`${cellClass} transition-colors hover:bg-neutral-50`}>
          {body}
        </Link>
      ) : (
        <div key={item.label} className={cellClass}>
          {body}
        </div>
      );
    })}
  </div>
);
